/** @format */

import { FC, useState, useCallback } from "react";
import { Filter, X } from "lucide-react";
import { Popover } from "@radix-ui/themes";
import type { Organization } from "../../types/organization";
import type { SopListFilters as SopListFiltersType } from "../../store/useSopStore";

export function countFilters(f: SopListFiltersType): number {
  let n = 0;
  if (f.dateFrom?.trim()) n++;
  if (f.dateTo?.trim()) n++;
  if (f.sopTitle?.trim()) n++;
  if (f.organizationId?.trim()) n++;
  if (f.creatorEmail?.trim()) n++;
  return n;
}

interface SOPListFiltersProps {
  organizations: Organization[];
  onApply: (filters: SopListFiltersType) => void;
  onClear: () => void;
  /** Number of filters currently applied (to show badge / Clear filters link). */
  activeFilterCount?: number;
  disabled?: boolean;
  /** When true, show the Organization dropdown and include organizationId in applied filters. SuperAdmin only. */
  showOrganizationFilter?: boolean;
}

const SOPListFilters: FC<SOPListFiltersProps> = ({
  organizations,
  onApply,
  onClear,
  activeFilterCount = 0,
  disabled = false,
  showOrganizationFilter = false,
}) => {
  const [open, setOpen] = useState(false);
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [sopTitle, setSopTitle] = useState("");
  const [organizationId, setOrganizationId] = useState("");
  const [creatorEmail, setCreatorEmail] = useState("");

  const getFilters = useCallback((): SopListFiltersType => {
    const f: SopListFiltersType = {};
    if (dateFrom.trim()) f.dateFrom = dateFrom.trim();
    if (dateTo.trim()) f.dateTo = dateTo.trim();
    if (sopTitle.trim()) f.sopTitle = sopTitle.trim();
    if (showOrganizationFilter && organizationId.trim())
      f.organizationId = organizationId.trim();
    if (creatorEmail.trim()) f.creatorEmail = creatorEmail.trim();
    return f;
  }, [
    dateFrom,
    dateTo,
    sopTitle,
    organizationId,
    creatorEmail,
    showOrganizationFilter,
  ]);

  const handleApply = () => {
    onApply(getFilters());
    setOpen(false);
  };

  const handleClearForm = () => {
    setDateFrom("");
    setDateTo("");
    setSopTitle("");
    setOrganizationId("");
    setCreatorEmail("");
    onClear();
    setOpen(false);
  };

  const inputClass =
    "w-full rounded-lg border border-slate-600/50 bg-slate-700/30 px-3 py-1.5 text-sm text-white placeholder-slate-400 focus:border-blue-500/50 focus:outline-none focus:ring-1 focus:ring-blue-500/30";
  const labelClass = "block text-xs font-medium text-slate-400 mb-0.5";

  return (
    <div className="flex flex-wrap items-center gap-2">
      <Popover.Root open={open} onOpenChange={setOpen}>
        <Popover.Trigger>
          <button
            type="button"
            disabled={disabled}
            className="inline-flex items-center gap-1.5 rounded-lg border border-slate-600/50 bg-slate-700/30 px-3 py-1.5 text-sm font-medium text-slate-300 hover:bg-slate-600/40 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Filter className="w-4 h-4" />
            Filters
            {activeFilterCount > 0 && (
              <span className="min-w-[1.25rem] rounded-full bg-blue-500/30 px-1.5 py-0.5 text-xs text-blue-300">
                {activeFilterCount}
              </span>
            )}
          </button>
        </Popover.Trigger>
        <Popover.Content width="320px" align="start" className="z-50">
          <div className="p-3 space-y-3 bg-slate-800 border border-slate-600/50 rounded-lg shadow-xl">
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label htmlFor="sop-filters-dateFrom" className={labelClass}>
                  Date from
                </label>
                <input
                  id="sop-filters-dateFrom"
                  type="date"
                  value={dateFrom}
                  onChange={(e) => setDateFrom(e.target.value)}
                  className={inputClass}
                  disabled={disabled}
                />
              </div>
              <div>
                <label htmlFor="sop-filters-dateTo" className={labelClass}>
                  Date to
                </label>
                <input
                  id="sop-filters-dateTo"
                  type="date"
                  value={dateTo}
                  onChange={(e) => setDateTo(e.target.value)}
                  className={inputClass}
                  disabled={disabled}
                />
              </div>
            </div>
            <div>
              <label htmlFor="sop-filters-sopTitle" className={labelClass}>
                SOP title
              </label>
              <input
                id="sop-filters-sopTitle"
                type="text"
                placeholder="Partial match"
                value={sopTitle}
                onChange={(e) => setSopTitle(e.target.value)}
                className={inputClass}
                disabled={disabled}
              />
            </div>
            {showOrganizationFilter && (
              <div>
                <label htmlFor="sop-filters-organization" className={labelClass}>
                  Organization
                </label>
                <select
                  id="sop-filters-organization"
                  value={organizationId}
                  onChange={(e) => setOrganizationId(e.target.value)}
                  className={inputClass}
                  disabled={disabled}
                >
                  <option value="">All</option>
                  {organizations.map((org) => (
                    <option key={org.orgId} value={org.orgId}>
                      {org.name}
                    </option>
                  ))}
                </select>
              </div>
            )}
            <div>
              <label htmlFor="sop-filters-creatorEmail" className={labelClass}>
                Creator email
              </label>
              <input
                id="sop-filters-creatorEmail"
                type="text"
                placeholder="Filter by creator"
                value={creatorEmail}
                onChange={(e) => setCreatorEmail(e.target.value)}
                className={inputClass}
                disabled={disabled}
              />
            </div>
            <div className="flex gap-2 pt-1">
              <button
                type="button"
                onClick={handleApply}
                disabled={disabled}
                className="flex-1 rounded-lg border border-blue-500/50 bg-blue-500/20 px-3 py-1.5 text-sm font-medium text-blue-300 hover:bg-blue-500/30 transition-colors disabled:opacity-50"
              >
                Apply
              </button>
              <button
                type="button"
                onClick={handleClearForm}
                disabled={disabled}
                className="inline-flex items-center gap-1 rounded-lg border border-slate-600/50 bg-slate-700/30 px-3 py-1.5 text-sm font-medium text-slate-300 hover:bg-slate-600/40 transition-colors disabled:opacity-50"
              >
                <X className="w-3.5 h-3.5" />
                Clear
              </button>
            </div>
          </div>
        </Popover.Content>
      </Popover.Root>
      {activeFilterCount > 0 && (
        <button
          type="button"
          onClick={onClear}
          disabled={disabled}
          className="text-sm text-slate-400 hover:text-slate-300 underline disabled:opacity-50"
        >
          Clear filters
        </button>
      )}
    </div>
  );
};

export default SOPListFilters;
